import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Clock3, List, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { slug } from "../lib/format";
import { scrollToHeading } from "../lib/useActiveHeading";
import { useDialogFocus } from "../lib/useDialogFocus";
import { TracingBeam } from "./TracingBeam";

function chapterLabel(count: number) {
  const last = count % 10; const teen = count % 100;
  if (teen >= 11 && teen <= 14) return `${count} глав`;
  if (last === 1) return `${count} глава`;
  if (last >= 2 && last <= 4) return `${count} главы`;
  return `${count} глав`;
}

/**
 * Оглавление для узких экранов. Боковая колонка (`.toc`, `.public-toc`) на ≤820px
 * скрыта, и без этой панели «Сериал» на 60 минут читается вообще без навигации по
 * главам. Заголовки и активная глава приходят готовыми из `useReaderChrome` —
 * здесь только представление, второй логики оглавления в проекте быть не должно.
 */
export function ReaderTocSheet({ headings, activeHeading, readTime, night = false }: {
  headings: string[]; activeHeading: string; readTime: number; night?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const dialogRef = useDialogFocus<HTMLDivElement>(open, () => setOpen(false));
  // Панель нужна только там, где спрятана боковая колонка: на широком экране
  // читатель уходит на десктопную раскладку, а открытый sheet остался бы висеть.
  useEffect(() => {
    if (!open) return;
    const wide = window.matchMedia("(min-width: 821px)");
    const close = () => { if (wide.matches) setOpen(false); };
    wide.addEventListener("change", close);
    return () => wide.removeEventListener("change", close);
  }, [open]);

  if (!headings.length) return null;

  function pick(id: string) {
    setOpen(false);
    scrollToHeading(id);
  }

  return <>
    <button
      type="button"
      className="toc-sheet-button"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={() => setOpen(true)}
    ><List /><span>Главы</span><i aria-hidden="true">{headings.length}</i></button>
    {createPortal(<AnimatePresence>{open && <motion.div
      className="toc-sheet-backdrop"
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduceMotion ? 0 : .2 }}
      onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}
    >
      <motion.div
        ref={dialogRef}
        className={`toc-sheet${night ? " toc-sheet-night" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="toc-sheet-title"
        tabIndex={-1}
        initial={reduceMotion ? false : { y: "100%" }}
        animate={{ y: 0 }}
        exit={reduceMotion ? { opacity: 0 } : { y: "100%" }}
        transition={{ duration: reduceMotion ? 0 : .28, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className="toc-sheet-grip" aria-hidden="true" />
        <header>
          <div><span className="eyebrow">Оглавление</span><b id="toc-sheet-title">{chapterLabel(headings.length)}</b></div>
          <button type="button" onClick={() => setOpen(false)} aria-label="Закрыть оглавление"><X size={17} /></button>
        </header>
        <TracingBeam headingIds={headings.map(slug)}><nav>{headings.map((heading, index) => {
          const id = slug(heading);
          const active = id === activeHeading;
          return <a
            key={`${index}-${heading}`}
            className={active ? "active" : ""}
            aria-current={active ? "true" : undefined}
            href={`#${id}`}
            onClick={(event) => { event.preventDefault(); pick(id); }}
          ><span>{String(index + 1).padStart(2, "0")}</span>{heading}</a>;
        })}</nav></TracingBeam>
        <footer><Clock3 size={16} /><span>{readTime} мин спокойного чтения</span></footer>
      </motion.div>
    </motion.div>}</AnimatePresence>, document.body)}
  </>;
}
